import React, { useState } from "react";
import { MapPin, Phone, Mail } from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for the field being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    try {
      const response = await fetch(
        "https://www.elamtravelstrichy.com/api/send_mail.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(formData).toString(),
        }
      );
      if (response.ok) {
        setSubmissionStatus(
          "Thanks for your Message. We will get back to you soon!"
        );
        setFormData({
          name: "",
          email: "",
          message: "",
          phone: "",
          vehicle: "",
        }); // Clear form
        setErrors({}); // Clear errors
      } else {
        setSubmissionStatus("Error submitting form. Please try again.");
      }
    } catch (error) {
      setSubmissionStatus("Network error. Please try again.");
    }
  };

  return (
    <section
      id="contact"
      className="py-20 bg-white"
      aria-labelledby="contact-heading"
    >
      <div className="container mx-auto px-4">
        <h2
          id="contact-heading"
          className="text-3xl md:text-4xl font-bold text-center mb-4"
        >
          Get In Touch
        </h2>
        <p className="text-xl text-center text-gray-600 mb-12">
          Contact Us for Any Queries
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <MapPin className="w-6 h-6 text-teal-600 mt-1" />
              <div className="text-left">
                <h3 className="text-2xl font-semibold mb-2">Address</h3>
                <p className="text-xl text-gray-600">
                  1/189J, Vasancity 5th Cross, Vayalur Road,
                  <br />
                  Trichy - 620102
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Phone className="w-6 h-6 text-teal-600 mt-1" />
              <div className="text-left">
                <h3 className="text-2xl font-semibold mb-2">Call Us</h3>
                <p className="text-xl text-gray-600">+91 99409 06161</p>
                <p className="text-xl text-gray-600">+91 98436 54847 </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <Mail className="w-6 h-6 text-teal-600 mt-1" />
              <div className="text-left">
                <h3 className="text-2xl font-semibold mb-2">Email Us</h3>
                <p className="text-xl text-gray-600">
                  info@elamtravelstrichy.com
                </p>
              </div>
            </div>
          </div>

          <form
            className="space-y-6"
            onSubmit={handleSubmit}
            aria-labelledby="contact-form"
          >
            <fieldset>
              <legend className="sr-only">Contact Form</legend>

              <div className="mb-6">
                <label htmlFor="name" className="sr-only">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  placeholder="Your Name"
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  aria-invalid={errors.name ? "true" : "false"}
                />
                {errors.name && (
                  <p
                    className="text-left text-red-500 text-sm mt-1"
                    id="name-error"
                  >
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  onChange={handleChange}
                  aria-invalid={errors.email ? "true" : "false"}
                />
                {errors.email && (
                  <p
                    className="text-left text-red-500 text-sm mt-1"
                    id="email-error"
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label htmlFor="phone" className="sr-only">
                  Phone
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  onChange={handleChange}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="vehicle" className="sr-only">
                  Vehicle
                </label>
                <input
                  id="vehicle"
                  type="text"
                  name="vehicle"
                  placeholder="Vehicle"
                  value={formData.vehicle}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  onChange={handleChange}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="sr-only">
                  Message
                </label>
                <textarea
                  id="message"
                  placeholder="Message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    errors.message ? "border-red-500" : "border-gray-300"
                  }`}
                  aria-invalid={errors.message ? "true" : "false"}
                ></textarea>
                {errors.message && (
                  <p
                    className="text-left text-red-500 text-sm mt-1"
                    id="message-error"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-orange-400 hover:bg-orange-500 text-black font-semibold py-3 rounded-lg transition"
                aria-label="Send Message"
              >
                Send Message
              </button>
            </fieldset>

            {submissionStatus && (
              <p
                className={`mt-4 text-center ${
                  submissionStatus.includes("Error")
                    ? "text-red-500"
                    : "text-green-500"
                }`}
              >
                {submissionStatus}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
